import { DateTime } from "luxon"

import type { DateRange } from "@mui/x-date-pickers-pro/models"

export const inclusiveRangeEndingOn = (
  endDate: string,
  offset: { months?: number; years?: number }
): DateRange<DateTime> | null => {
  const end = DateTime.fromISO(endDate).startOf("day")
  if (!end.isValid) {
    return null
  }

  const start = end.minus(offset).startOf("day")
  if (!start.isValid) {
    return null
  }

  return [start, end]
}

/** First day of the calendar month one year before a selected range start. */
export const startOfMonthOneYearBefore = (rangeStart: string): string => {
  const start = DateTime.fromISO(rangeStart)
    .minus({ years: 1 })
    .startOf("month")
  return start.isValid ? (start.toISODate() ?? rangeStart) : rangeStart
}

/** One year ending on the latest bill start, or on today when that start is missing. */
export const openingBillDateRange = (
  latestStartDate: string | null | undefined,
  today: string
): DateRange<DateTime> | null => {
  const latest = DateTime.fromISO(latestStartDate?.trim() ?? "").startOf("day")
  const anchor = latest.isValid ? latest.toISODate() : null
  return inclusiveRangeEndingOn(anchor ?? today, { years: 1 })
}

/** Same calendar month one year earlier, or null when `monthKey` is not `yyyy-MM`. */
export const subtractYearFromMonthKey = (monthKey: string): string | null => {
  const month = DateTime.fromFormat(monthKey, "yyyy-MM")
  if (!month.isValid) {
    return null
  }

  return month.minus({ years: 1 }).toFormat("yyyy-MM")
}

export const formatBillMonthLabel = (
  monthKey: string,
  language: string
): string => {
  const date = DateTime.fromISO(`${monthKey}-01`)
  if (!date.isValid) {
    return monthKey
  }

  return date.setLocale(language).toFormat("LLL yyyy")
}

export const monthKeysBetween = (start: string, end: string): string[] => {
  let cursor = DateTime.fromISO(start).startOf("month")
  const last = DateTime.fromISO(end).startOf("month")
  if (!cursor.isValid || !last.isValid || cursor > last) {
    return []
  }

  const keys: string[] = []
  while (cursor <= last) {
    keys.push(cursor.toFormat("yyyy-MM"))
    cursor = cursor.plus({ months: 1 })
  }

  return keys
}
